import { Cell, Grid } from 'baseui/layout-grid'
import { DateTime } from 'luxon'
import React, { HTMLAttributes } from 'react'
import { useStyletron } from '../../../../theme'
import Month from './Month'
import YearSwitchButton, { DIRECTION } from './YearSwitchButton'

const months = new Array(12).fill(0)

/**
 * @interface MonthPickerMenuProps
 * @member {{text: string, icon?: string}} caption specified for caption withj optional icon
 * @member {string} maxDate DateTime
 * @member {string} minDate DateTime
 * @member {(value: DateTime) => void} onChange function executed on month selection
 * @member {DateTime} value of currently selected month
 */
export interface MonthPickerMenuProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
    caption?: { text: string; icon?: string }
    maxDate?: DateTime
    minDate?: DateTime
    onChange?: (value: DateTime) => void
    value?: DateTime
}

export const MonthPickerMenu = ({
    caption,
    minDate,
    maxDate,
    onChange,
    value = DateTime.now().minus({ month: 1 }).startOf('month'),
    className,
    ...props
}: MonthPickerMenuProps) => {
    const [year, setYear] = React.useState(value.startOf('year'))
    const [css, theme] = useStyletron()
    const [month, setMonth] = React.useState(value.toFormat('yyyy MM'))

    return (
        <div
            className={`${css({
                width: '360px',
                backgroundColor: 'white',
                borderRadius: '30px',
                boxShadow: `${theme.sizing.scale100} ${theme.sizing.scale100} ${theme.sizing.scale800} ${theme.sizing.scale200} rgba(50, 53, 78, 0.08)`,
                zIndex: 2
            })} ${className}`}
            {...props}
        >
            <div className={css({ display: 'flex', flexDirection: 'column', padding: '16px' })}>
                <div className={css({ display: 'flex', marginBottom: theme.sizing.scale400 })}>
                    <YearSwitchButton
                        onClick={() => setYear((curr) => curr.minus({ year: 1 }))}
                        disabled={!!minDate && year.minus({ year: 1 }).toFormat('yyyy') < minDate.toFormat('yyyy')}
                        direction={DIRECTION.LEFT}
                    />
                    <div className={css({ flex: '1', textAlign: 'center', ...theme.typography.LabelMedium })}>
                        {year.toFormat('yyyy')}
                    </div>
                    <YearSwitchButton
                        onClick={() => setYear((curr) => curr.plus({ year: 1 }))}
                        disabled={!!maxDate && year.plus({ year: 1 }).toFormat('yyyy') > maxDate.toFormat('yyyy')}
                        direction={DIRECTION.RIGHT}
                    />
                </div>
                <Grid gridColumns={[4, 6, 6]} gridGaps={12} gridMargins={0} gridGutters={8}>
                    {months.map((_v, i) => (
                        <Cell
                            key={i}
                            span={1}
                            overrides={{
                                Cell: {
                                    style: {
                                        display: 'flex !important',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }
                                }
                            }}
                        >
                            <Month
                                selected={month === year.set({ month: i + 1 }).toFormat('yyyy MM')}
                                disabled={
                                    (!!maxDate &&
                                        year
                                            .set({ month: i + 1 })
                                            .diff(maxDate, 'days')
                                            .get('day') > 0) ||
                                    (!!minDate &&
                                        year
                                            .set({ month: i + 1 })
                                            .diff(minDate, 'days')
                                            .get('day') < 0)
                                }
                                value={year.set({ month: i + 1 })}
                                onClick={(value) => {
                                    const monthSelected = value.toFormat('yyyy MM')
                                    setMonth(monthSelected)
                                    onChange && onChange(value)
                                }}
                            />
                        </Cell>
                    ))}
                </Grid>
                {caption && (
                    <div
                        className={css({
                            display: 'flex',
                            marginTop: '8px',
                            padding: '8px',
                            alignItems: 'center',
                            backgroundColor: theme.colors.primary50,
                            color: theme.colors.primary,
                            borderRadius: '16px'
                        })}
                    >
                        {caption?.icon && (
                            <i
                                className={caption?.icon}
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    fontSize: theme.typography.HeadingXSmall.fontSize
                                }}
                            />
                        )}
                        <div className={css({ ...theme.typography.LabelSmall, paddingLeft: '8px' })}>
                            {caption.text}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
